import { formatInTimeZone } from "date-fns-tz";
import { Icon, Typography } from "@/components/atoms";
import { BookingStatus } from "@/redux/slices/booking/types";
import { capitalize, parseHtml } from "@/utils";
import { BookingStatusIcon } from "../common/BookingStatusIcon/BookingStatusIcon";
import { statusMapping } from "../fixtures";

export const getBookingHistoryIcon = (name: string) => {
  switch (true) {
    case name.includes("view"):
      return <Icon name="Eye" size="lg" className="shrink-0 text-primary" />;
    case name.includes("email"):
      return <Icon name="Sms" size="lg" className="shrink-0 text-primary" />;
    case name.includes("voice"):
      return <Icon name="Call" size="lg" className="shrink-0 text-primary" />;
    default:
      return <Icon name="ArchiveBook" size="lg" className="shrink-0 text-primary" />;
  }
};

export const isBookingHistoryEventSystemType = (name: string) => {
  return ["email", "sms", "call"].includes(name.toLowerCase());
};

export const getBookingHistoryEventText = (eventType: string, eventName: string, fieldName?: string) => {
  const formatName = (name?: string) => (name ? capitalize(name.replace("_", " ").replace("-", " ")) : "");

  switch (eventType) {
    case "view":
      return "Viewed Job";
  }

  switch (eventName) {
    case "email":
      return "Email Type";
    case "job.update":
      return "Job Updated";
    case "job.new":
      return "Job Created";
    case "driver.accepted.job":
      return "Driver Accepted Job";
    case "flight.arrival.changed":
      return "Flight Arrival Changed";
    case "job.updated.admin-details":
      return "Admin Details Updated";
    case "job.passenger-dropped-off":
      return "Driver marked passenger dropped off";
    case "job.passenger-dropped-off-edited":
      return "Passenger dropped off time edited";
    case "driver.passenger-picked-up.job":
      return "Driver marked passenger picked up";
    case "driver.passenger-picked-up.job-edited":
      return "Passenger picked up time edited";
    case "job.close.awaiting-review.post":
      return "Job has been closed pending review of extras";
    case "job.close.post":
      return "Job has been closed";
    case "driver.declined.job":
      return "Driver has declined job";
    case "driver.en-route.job":
      return "Driver is on-way to pickup point";
    case "job.passenger-close-to-destination":
      return "Driver has marked themselves as approaching the destination";
    case "booking.cancel.post":
      return "Job has been closed";
    case "job.commenced":
      return "Job has commenced";
    case "job.offload.recall":
      return "Job offload recalled";
    case "job.reopen":
      return "Job has been reopened";
    case "job.offloaded":
      return "Job offloaded to network";
    case "job.payment.adjustment":
      return "Payment Adjustment";
    case "job.viewed":
      return "Viewed Job";
    case "job.paid":
      return "Job Paid";
    case "job.price.adjustment":
      return `Job Pricing Change: ${formatName(fieldName)}`;
    case "job.cancelled.field":
      return `Job Cancelled: ${formatName(fieldName)}`;
    case "job.close.field":
      return `Job Closed: ${formatName(fieldName)}`;
    case "job.admin.edit.field":
      return `Admin Edit: ${formatName(fieldName)}`;
    case "job.edit.field":
      return `Job Edited: ${formatName(fieldName)}`;
    case "job.status.change":
      return `Status Changed`;
    default:
      return formatName(fieldName || eventType);
  }
};

/**
 * A history before/after value, rendered readably.
 *
 * Time fields arrive as UNIX EPOCH SECONDS, so a pickup move renders as "To: 1787693700" — technically
 * the value, and useless to a dispatcher. Those are formatted in the ROW's timezone with the profile's
 * own formats, so they read the same way as the timestamp in the Date column beside them.
 *
 * Keyed on the VALUE, not on the field name, so a row starts formatting correctly the moment the backend
 * begins sending values for it — `flight.arrival.changed` is written as a bare marker today and will not
 * need a frontend change when that lands.
 *
 * The discriminator is ten digits with NO leading zero, in [1e9, 2e9) — 2001-09-09 to 2033-05-18. Both
 * halves earn their place. A bare `\d{10}` spans 1970 to 2286 and swallows a 10-digit AU phone number,
 * so editing a passenger's `0412345678` would render "24/01/1983"; the leading-zero rule excludes those
 * and the upper bound excludes a 10-digit US number or reference. Anything else — a status, a name, a
 * price, free text, a millisecond epoch — is returned untouched rather than mangled into a date.
 */
export const formatBookingHistoryValue = (value: string, timezone: string, dateFormat: string, timeFormat: string): string => {
  if (!/^[1-9]\d{9}$/.test(value)) return value;
  const seconds = Number(value);
  if (seconds < 1_000_000_000 || seconds >= 2_000_000_000) return value;
  const asDate = new Date(seconds * 1000);
  if (isNaN(asDate.getTime())) return value;
  try {
    return formatInTimeZone(asDate, timezone, `${dateFormat} ${timeFormat}`);
  } catch {
    // An unusable timezone is a payload problem; the raw value beats crashing the whole table.
    return value;
  }
};

export const getBookingHistoryActionText = (name: string, from?: string, to?: string) => {
  const render = (value: string) => {
    const statusObj = statusMapping[value as BookingStatus];

    if (name === "job_status" && statusObj) {
      return (
        <span className="inline-flex items-center gap-1">
          <BookingStatusIcon
            status={{
              color: statusObj.color,
              icon: statusObj.icon,
            }}
          />
          {statusObj.legend}
        </span>
      );
    }
    return <span className="inline">{parseHtml(value, false)}</span>;
  };

  return (
    <div className="flex flex-col ">
      {to !== undefined && (
        <Typography variant="small">
          <span className="inline-block min-w-[50px]">To:</span> {to ? render(to) : <span className="text-neutral-dark-gray">Empty</span>}
        </Typography>
      )}
      {from !== undefined && (
        <Typography variant="small">
          <span className="inline-block min-w-[50px]">From:</span>{" "}
          {from ? render(from) : <span className="text-neutral-dark-gray">Empty</span>}
        </Typography>
      )}
    </div>
  );
};
